import { type Store } from '@reduxjs/toolkit';
import { StandardErrorCodes, SYNC_HORIZON_HEADER } from '@soulhx/fs-common';
import { type RootState } from '../stores/Store.js';
import { updateToken } from '../stores/PersistedUI.js';

// `Store.ts` needs listener middleware (from CommentSyncMiddleware.ts /
// GlobalSyncMiddleware.ts, which import this module) wired into its
// middleware chain, so this module can't import `store` as a value from
// Store.ts without creating a real (value-level) import cycle. Instead
// Store.ts hands its singleton to us via registerSyncStore() right after
// creating it - the `RootState` import above is type-only and erased, so
// there's no runtime cycle.
let syncStore: Store<RootState> | undefined;

export const registerSyncStore = (newStore: Store<RootState>): void => {
  syncStore = newStore;
};

export interface SyncFetchOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  body?: unknown;
  // lets a request outlive the page unloading (e.g. a tab close right after
  // a local edit) instead of being aborted mid-flight - see flushSettingsOnHide
  keepalive?: boolean;
}

/**
 * Thrown by {@link syncFetch} for any non-2xx (other than 401, which
 * reloads). Carries the HTTP status and, when the body is one of our APIs'
 * standard error responses, its `errorCode` - see {@link isItemGoneError}.
 */
export class SyncFetchError extends Error {
  constructor(
    message: string,
    public status: number,
    public errorCode?: number
  ) {
    super(message);
    Object.setPrototypeOf(this, SyncFetchError.prototype);
  }
}

/**
 * True when a push failed because the item no longer exists server-side
 * (e.g. deleted on another device while a local edit was pending) - the
 * API's own `NotFoundError`, not just any 404. A bare 404 from a proxy or
 * ingress (bad deploy, misrouting) doesn't carry the API's `errorCode`, and
 * must not be mistaken for "gone": callers drop the pending local row on
 * this, which would otherwise lose the user's edit.
 */
export const isItemGoneError = (error: unknown): boolean =>
  error instanceof SyncFetchError && error.status === 404 && error.errorCode === StandardErrorCodes.NotFound;

// sends the request, handling auth and non-2xx statuses; the caller parses
// the (successful) response
const send = async ({ method, path, body, keepalive }: SyncFetchOptions): Promise<Response> => {
  if (!syncStore) throw new Error('syncFetch: called before registerSyncStore()');

  const token = syncStore.getState().persistedUI.token;

  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    keepalive,
  });

  if (response.status === 401) {
    syncStore.dispatch(updateToken(''));
    window.location.reload();
    throw new Error(`syncFetch: unauthorized (${method} ${path})`);
  }

  if (!response.ok) {
    let errorCode: number | undefined;
    try {
      const parsed = JSON.parse(await response.text());
      if (typeof parsed?.errorCode === 'number') errorCode = parsed.errorCode;
    } catch {
      // not one of our APIs' JSON error bodies - leave errorCode undefined
    }

    throw new SyncFetchError(
      `syncFetch: ${method} ${path} failed with status ${response.status}`,
      response.status,
      errorCode
    );
  }

  return response;
};

const parseBody = async <T>(response: Response): Promise<T> => {
  const text = await response.text();
  return (text.length > 0 ? JSON.parse(text) : undefined) as T;
};

export const syncFetch = async <T>(options: SyncFetchOptions): Promise<T> => parseBody<T>(await send(options));

export interface SyncPullResult<T> {
  data: T;
  /** see `SYNC_HORIZON_HEADER` - undefined from an API that doesn't send one */
  horizon?: string;
}

/** A delta-sync `GET`: {@link syncFetch}, plus the response's sync horizon. */
export const syncPull = async <T>(path: string): Promise<SyncPullResult<T>> => {
  const response = await send({ method: 'GET', path });
  // `headers` is typed as always present, but a hand-rolled fetch stub (as
  // the tests use) may leave it out - treat that as "no horizon"
  const headers = response.headers as Headers | undefined;
  const horizon = headers?.get(SYNC_HORIZON_HEADER) ?? undefined;
  return { data: await parseBody<T>(response), horizon };
};
